import MnemonicTable from "@features/graph/details/components/MnemonicTable"
import { useLocalGraphScenario } from "@hooks/useLocalGraphScenario"
import { ExpandMore } from "@mui/icons-material"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material"
import { useGetGraphForecastMnemonicQuery } from "@store/services/graphsService"
import { getTableRowsFromCsvContent } from "@utils/file"
import { Node } from "reactflow"
import { useLocalStorage } from "usehooks-ts"
import { mergeMnemonicDataWithEmbeddedScenario } from "@features/graph/details/components/MnemonicTable/helpers"

type ForecastAccordionProps = {
  graphKey?: string
  editable: boolean
  node: Node
}

const ForecastAccordion = (props: ForecastAccordionProps) => {
  const [isExpanded, setIsExpanded] = useLocalStorage(`forecastAccordionExpanded`, true)

  const graphKey = props.graphKey ?? ""
  const mnemonic = props.node.data.display["Forecast Mnem"]
  const { scenario, updateScenario } = useLocalGraphScenario(graphKey)
  const getMnemonicQuery = useGetGraphForecastMnemonicQuery(
    {
      key: graphKey,
      mnemonic,
      isScenario: false,
    },
    { skip: !graphKey },
  )

  if (getMnemonicQuery.isLoading) {
    return (
      <Grid container justifyContent="center" sx={{ p: 1 }}>
        <Grid item>
          <CircularProgress />
        </Grid>
      </Grid>
    )
  }

  if (!getMnemonicQuery.data || getMnemonicQuery.data.empty) return <></>

  // rows for this mnemonic, read from the live (locally edited) scenario CSV rather than the
  // node's data.scenario snapshot, so the table reflects edits immediately
  const embeddedScenarioRows = scenario
    ? getTableRowsFromCsvContent(scenario).filter((row) => row.Mnem === mnemonic)
    : props.node.data.scenario

  const filteredData = mergeMnemonicDataWithEmbeddedScenario(
    getMnemonicQuery.data,
    embeddedScenarioRows,
  )

  const handleEditMnemonic = async (timestep: string, newValue: string) => {
    await updateScenario({
      Date: timestep,
      Value: newValue,
      Mnem: mnemonic,
      Scenario: "",
    })
  }

  return (
    <Grid item sx={{ width: isExpanded ? "auto" : "100%" }}>
      <Accordion expanded={isExpanded} onChange={(_event, newValue) => setIsExpanded(newValue)}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography sx={{ fontWeight: 700 }}>Forecast Data</Typography>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 0 }}>
          {getMnemonicQuery.data ? (
            <MnemonicTable
              data={filteredData}
              editable={props.editable}
              onEdit={handleEditMnemonic}
            />
          ) : (
            <Typography>No data for selected forecast</Typography>
          )}
        </AccordionDetails>
      </Accordion>
    </Grid>
  )
}

export default ForecastAccordion
